import { useEffect, useState } from 'react';

/**
 * "Typing" feel of the panel without streaming: the newest answer appears word by word (about 0.7 s in total, once
 * per message), and the busy line names what the assistant is doing. Respects "reduce motion": then the text is
 * shown at once. Screen readers get the full text from the live region either way (it is set at the end).
 */
const reduced = () => typeof window === 'undefined' || typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const revealed = new Set<string>();

export function useReveal(text: string, enabled: boolean, id = text): string {
  const animate = enabled && !!text && !revealed.has(id) && !reduced();
  const words = text.split(/(\s+)/);
  const [n, setN] = useState(animate ? 0 : words.length);
  useEffect(() => {
    if (!animate) return;
    revealed.add(id);
    const step = Math.max(1, Math.ceil(words.length / 24));
    const timer = setInterval(() => {
      setN((x) => {
        const next = Math.min(words.length, x + step);
        if (next >= words.length) clearInterval(timer);
        return next;
      });
    }, 28);
    return () => clearInterval(timer);
    // the text of a stored message never changes; animate once per id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return n >= words.length ? text : words.slice(0, n).join('');
}

export type BusyStep = 'read' | 'check' | 'write';
const STEPS: BusyStep[] = ['read', 'check', 'write'];

/** While a turn runs: "Ich schaue in deine Daten" → "Ich prüfe die Regeln" → "Ich formuliere die Antwort". */
export function useBusySteps(busy: boolean, ms = 1800): BusyStep {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!busy) {
      setI(0);
      return;
    }
    const timer = setInterval(() => setI((x) => Math.min(STEPS.length - 1, x + 1)), ms);
    return () => clearInterval(timer);
  }, [busy, ms]);
  return STEPS[i]!;
}
